import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { OptionEtapeBonTravail } from 'src/app/entites/mecanique/option-etape-bon-travail';

@Component({
  selector: 'option-etape-bon-travail',
  templateUrl: './option-etape-bon-travail.component.html',
  providers: [MessageService, ConfirmationService]
})
export class OptionEtapeBonTravailComponent implements OnInit, OnChanges {
  @Input({ required: true }) options: OptionEtapeBonTravail[] = [];
  /** Server id of the retained option (`null` / `0` = none). */
  @Input() optionSelectionnee: number | null = null;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() modifySelection: boolean = false;
  @Input() radioGroupName = 'option-soumission';
  /** Dense read-only list for collapsed table rows. */
  @Input() compact = false;

  @Output() optionSelectionneeChange: EventEmitter<number | null> = new EventEmitter<number | null>();
  @Output() optionsChange: EventEmitter<OptionEtapeBonTravail[]> = new EventEmitter<OptionEtapeBonTravail[]>();
  @Output() onChange: EventEmitter<OptionEtapeBonTravail> = new EventEmitter<OptionEtapeBonTravail>();
  @Output() optionSupprimee = new EventEmitter<OptionEtapeBonTravail>();

  get _optionSelectionnee(): number | null {
    return this.optionSelectionnee ?? null;
  }

  set _optionSelectionnee(value: number | null) {
    this.optionSelectionnee = value ?? null;
    this.optionSelectionneeChange.emit(this.optionSelectionnee);
  }

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    if (this.compact && this.readonly) {
      return;
    }
    if (this.options == null || this.options.length == 0) {
      this.options = [];
      this.addOption();
      this.optionsChange.emit(this.options);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.options?.length) {
      return;
    }

    if (changes['readonly'] && !changes['readonly'].currentValue && !this.isLastOptionEmpty()) {
      this.addOption();
    } else if (changes['readonly'] && changes['readonly'].currentValue && this.isLastOptionEmpty()) {
      this.options.splice(this.options.length - 1, 1);
    }
  }

  /** Stable track key: server id when persisted, else ephemeral client id. */
  trackOption(option: OptionEtapeBonTravail): string | number {
    if (option.optionEtapeBonTravailId > 0) {
      return option.optionEtapeBonTravailId;
    }
    if (!option.id) {
      option.id = this.uuid();
    }
    return option.id;
  }

  /** Radio value: server id, or `0` for the unsaved draft row. */
  valeurRadio(option: OptionEtapeBonTravail): number {
    return option.optionEtapeBonTravailId > 0 ? option.optionEtapeBonTravailId : 0;
  }

  estSelectionnee(option: OptionEtapeBonTravail): boolean {
    const selected = this._optionSelectionnee;
    if (selected == null || selected === 0) {
      return false;
    }
    return option.optionEtapeBonTravailId === selected;
  }

  addOption() {
    this.options.push({
      id: this.uuid(),
      optionEtapeBonTravailId: 0,
      description: '',
      etapeBonTravailId: 0,
      montantPieces: null,
      nbHeures: null,
      indAFaire: false
    });
    this.optionsChange.emit(this.options);
  }

  removeOption(option: OptionEtapeBonTravail) {
    this.confirmationService.confirm({
      message: 'Voulez-vous supprimer cette option ?',
      accept: () => {
        if (this.estSelectionnee(option)) {
          const remaining = this.options.filter(o => o !== option && o.optionEtapeBonTravailId > 0);
          this._optionSelectionnee = remaining[0]?.optionEtapeBonTravailId ?? null;
        }
        this.options = this.options.filter(o => o !== option);
        this.optionsChange.emit(this.options);
        this.optionSupprimee.emit(option);
        this.messageService.add({ severity: 'success', summary: 'Succès', detail: 'Option supprimée' });
      }
    });
  }

  surChangement(index: number) {
    const option = this.options[index];
    if (!option) {
      return;
    }
    this.onChange.emit(option);

    queueMicrotask(() => {
      if (!this.readonly && !this.compact) {
        if (!this.isLastOptionEmpty()) {
          this.addOption();
        } else if (this.options.length > 1) {
          this.options.splice(this.options.length - 1, 1);
          this.optionsChange.emit(this.options);
        }
      }
    });
  }

  changementOption(option: OptionEtapeBonTravail) {
    const value = this.valeurRadio(option);
    this.optionSelectionneeChange.emit(value > 0 ? value : 0);
  }

  /** Options shown in compact mode (excludes trailing empty add row). */
  optionsAffichables(): OptionEtapeBonTravail[] {
    if (!this.options?.length) {
      return [];
    }
    if ((this.compact || this.readonly) && this.isLastOptionEmpty()) {
      return this.options.slice(0, -1);
    }
    return this.options;
  }

  isLastOptionEmpty(): boolean {
    if (this.options.length === 0) {
      return false;
    }

    const lastOption = this.options[this.options.length - 1];
    return (
      (lastOption.montantPieces === null || lastOption.montantPieces === 0 || lastOption.montantPieces === undefined) &&
      (lastOption.nbHeures === null || lastOption.nbHeures === 0) &&
      (lastOption.description === '' || lastOption.description === undefined)
    );
  }

  private uuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
